import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useSocketEvent } from '../context/SocketContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { Stethoscope, Plus, Save, Pencil, Trash2, Search, IndianRupee, Layers } from 'lucide-react';
import ViewModeSwitcher, { useViewMode } from '../components/ViewModeSwitcher';

const CATEGORIES = [
  { value: 'restorative',  label: 'Restorative'  },
  { value: 'preventive',   label: 'Preventive'   },
  { value: 'surgical',     label: 'Surgical'      },
  { value: 'orthodontic',  label: 'Orthodontic'  },
  { value: 'cosmetic',     label: 'Cosmetic'      },
  { value: 'prosthetic',   label: 'Prosthetic'   },
  { value: 'other',        label: 'Other'         },
];

const CAT_COLORS = {
  restorative: { bg: 'var(--color-primary-soft)',  color: 'var(--color-primary)'  },
  preventive:  { bg: 'var(--color-success-soft)',  color: 'var(--color-success)'  },
  surgical:    { bg: 'var(--color-danger-soft)',   color: 'var(--color-danger)'   },
  orthodontic: { bg: 'var(--color-warning-soft)',  color: 'var(--color-warning)'  },
  cosmetic:    { bg: 'var(--color-accent-soft)',   color: 'var(--color-accent)'   },
  prosthetic:  { bg: 'var(--color-info-soft)',     color: 'var(--color-info)'     },
  other:       { bg: 'var(--color-border)',        color: 'var(--color-ink-soft)' },
};

function fmt(n) { return (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 }); }

const emptyForm = { name: '', category: 'other', description: '', price: '' };

// Compact inline edit form used inside grid-mode cards (flat grid and grouped grid)
function GridEditForm({ t, editForm, setEditForm, handleUpdate, setEditingId, saving, CATEGORIES }) {
  const efv = (field) => (e) => setEditForm(v => ({...v, [field]: e.target.value}));
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      <div className="field" style={{ marginBottom:0 }}>
        <label className="label" style={{ fontSize:11 }}>Name</label>
        <input className="input" style={{ fontSize:12, padding:'5px 8px' }} value={editForm.name||''} onChange={efv('name')}/>
      </div>
      <div className="field" style={{ marginBottom:0 }}>
        <label className="label" style={{ fontSize:11 }}>Category</label>
        <select className="select" style={{ fontSize:12, padding:'5px 8px' }} value={editForm.category||'other'} onChange={efv('category')}>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div className="field" style={{ marginBottom:0 }}>
        <label className="label" style={{ fontSize:11 }}>Price (₹)</label>
        <input className="input" style={{ fontSize:12, padding:'5px 8px' }} type="number" min="0" value={editForm.price||''} onChange={efv('price')} placeholder="0"/>
      </div>
      <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, cursor:'pointer' }}>
        <input type="checkbox" checked={!!editForm.isActive} onChange={e => setEditForm(v=>({...v,isActive:e.target.checked}))}/> Active
      </label>
      <div style={{ display:'flex', gap:4 }}>
        <button className="btn btn-primary btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0' }} onClick={() => handleUpdate(t._id)} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button className="btn btn-outline btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0' }} onClick={() => setEditingId(null)}>Cancel</button>
      </div>
    </div>
  );
}

function TreatmentCard({ t, catStyle, editingId, setEditingId, editForm, setEditForm, handleUpdate, setDeleteTarget, saving, CATEGORIES, plain }) {
  const efv = (field) => (e) => setEditForm(v => ({...v, [field]: e.target.value}));
  return (
    <div className={plain ? undefined : 'card'} style={{ padding: 14, opacity: t.isActive ? 1 : 0.55 }}>
      {editingId === t._id ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 10 }}>
            <div className="field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
              <label className="label">Name</label>
              <input className="input" value={editForm.name||''} onChange={efv('name')}/>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">Category</label>
              <select className="select" value={editForm.category||'other'} onChange={efv('category')}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label">Price (₹)</label>
              <input className="input" type="number" min="0" value={editForm.price||''} onChange={efv('price')} placeholder="0"/>
            </div>
            <div className="field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
              <label className="label">Description</label>
              <input className="input" value={editForm.description||''} onChange={efv('description')} placeholder="Optional description"/>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => handleUpdate(t._id)} disabled={saving}><Save size={13}/>{saving ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-outline btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, marginLeft:'auto', cursor:'pointer' }}>
              <input type="checkbox" checked={!!editForm.isActive} onChange={e => setEditForm(v=>({...v,isActive:e.target.checked}))}/> Active
            </label>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:14, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              {t.name}
              {!t.isActive && <span style={{ fontSize:11, color:'var(--color-ink-faint)', fontWeight:400 }}>(inactive)</span>}
            </div>
            {t.description && <div style={{ fontSize:12, color:'var(--color-ink-faint)', marginTop:2 }}>{t.description}</div>}
            <div style={{ marginTop:4 }}>
              <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100, background:catStyle.bg, color:catStyle.color }}>{CATEGORIES.find(c=>c.value===t.category)?.label||t.category}</span>
            </div>
          </div>
          <div style={{ fontWeight:700, fontSize:15, color:'var(--color-success)', display:'flex', alignItems:'center', gap:3 }}>
            {(t.price || 0) > 0 ? <><IndianRupee size={12} />{fmt(t.price)}</> : <span style={{ fontSize: 12 }}>No price</span>}
          </div>
          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
            <button className="btn btn-outline btn-sm" onClick={() => { setEditingId(t._id); setEditForm({...t}); }}><Pencil size={13}/> Edit</button>
            <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(t)}><Trash2 size={13}/> Remove</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TreatmentCatalogPage() {
  const { showToast } = useToast();

  const [listMode, setListMode] = useViewMode('treatment_view', 'list');
  const [treatments, setTreatments]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [catFilter, setCatFilter]       = useState('all');
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem('treatment_groupby') || 'none'); // 'none'|'category'
  const [showForm, setShowForm]         = useState(false);
  const [form, setForm]                 = useState(emptyForm);
  const [saving, setSaving]             = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [editForm, setEditForm]         = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/treatments', {
        params: { search, category: catFilter === 'all' ? '' : catFilter, includeInactive: 'true' },
      });
      setTreatments(res.data.treatments || []);
    } catch {
      showToast('Could not load treatment catalog.', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, catFilter, showToast]);

  useEffect(() => { load(); }, [load]);
  useSocketEvent('treatment:catalog-updated', () => load());

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.name.trim()) { showToast('Treatment name is required.', 'error'); return; }
    setSaving(true);
    try {
      const res = await api.post('/treatments', { ...form, price: Number(form.price) || 0 });
      showToast(`"${res.data.treatment.name}" added to catalog.`, 'success');
      setTreatments(prev => [...prev, res.data.treatment]);
      setForm(emptyForm);
      setShowForm(false);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not add treatment.', 'error');
    } finally { setSaving(false); }
  }

  async function handleUpdate(id) {
    setSaving(true);
    try {
      const res = await api.put(`/treatments/${id}`, { ...editForm, price: Number(editForm.price) || 0 });
      showToast('Treatment updated.', 'success');
      setTreatments(prev => prev.map(t => t._id === id ? res.data.treatment : t));
      setEditingId(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not update.', 'error');
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/treatments/${deleteTarget._id}`);
      showToast(`"${deleteTarget.name}" removed from catalog.`, 'success');
      setTreatments(prev => prev.filter(t => t._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not remove.', 'error');
    } finally { setDeleting(false); }
  }

  const sf = field => e => setForm(v => ({ ...v, [field]: e.target.value }));

  return (
    <AppLayout title="Treatment Catalog" subtitle="Manage the clinic's treatment list with names, categories, and prices">
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <ViewModeSwitcher mode={listMode} onChange={setListMode} />
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-faint)' }} />
          <input className="input" style={{ paddingLeft: 32 }} placeholder="Search treatments…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="select" style={{ minWidth: 150, fontWeight: 600 }} value={catFilter}
          onChange={e => setCatFilter(e.target.value)}>
          <option value="all">All categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Layers size={14} color="var(--color-ink-faint)"/>
          <select className="select" style={{ minWidth:150, fontWeight:600 }} value={groupBy} onChange={e => { setGroupBy(e.target.value); localStorage.setItem('treatment_groupby', e.target.value); }}>
            <option value="none">No grouping</option>
            <option value="category">Group by Category</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(v => !v)}>
          <Plus size={15} />{showForm ? 'Cancel' : 'Add Treatment'}
        </button>
      </div>

      {/* Add form — name, category, price, description. No discount here. */}
      {showForm && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Stethoscope size={16} color="var(--color-primary)" /> New Treatment
          </h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 12, marginBottom: 12 }}>
              <div className="field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                <label className="label">Treatment Name *</label>
                <input className="input" value={form.name} onChange={sf('name')}
                  placeholder="e.g. Root Canal Treatment, Scaling & Polishing" required />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="label">Category</label>
                <select className="select" value={form.category} onChange={sf('category')}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="label">Catalog Price (₹)</label>
                <input className="input" type="number" min="0" step="0.01"
                  value={form.price} onChange={sf('price')} placeholder="0" />
              </div>
              <div className="field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                <label className="label">Description (optional)</label>
                <input className="input" value={form.description} onChange={sf('description')}
                  placeholder="Brief notes about this treatment" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                <Save size={14} />{saving ? 'Saving…' : 'Save Treatment'}
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ display: 'grid', gap: 8 }}>
          {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 68, borderRadius: 8 }} />)}
        </div>
      ) : treatments.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center', color: 'var(--color-ink-faint)' }}>
          <Stethoscope size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ margin: 0, fontWeight: 600 }}>No treatments yet.</p>
          <p style={{ margin: '4px 0 0', fontSize: 13 }}>Add treatments above — they will appear as selectable options in patient profiles.</p>
        </div>
      ) : null}
      {!loading && treatments.length > 0 && groupBy === 'none' && listMode === 'grid' && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:10 }}>
            {treatments.map((t, i) => (
              <div key={t._id} className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
              <div className="card tilt3d-card" style={{ padding:12, display:'flex', flexDirection:'column', gap:6, opacity:t.isActive?1:0.6 }}>
                {editingId === t._id ? (
                  <GridEditForm t={t} editForm={editForm} setEditForm={setEditForm} handleUpdate={handleUpdate} setEditingId={setEditingId} saving={saving} CATEGORIES={CATEGORIES} />
                ) : (
                  <>
                    <div style={{ fontWeight:700, fontSize:13 }}>{t.name}</div>
                    <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{t.category}</div>
                    <div style={{ fontWeight:800, fontSize:18, color:'var(--color-primary)', marginTop:'auto', paddingTop:4 }}>₹{t.price||0}</div>
                    <div style={{ display:'flex', gap:4 }}>
                      <button className="btn btn-outline btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0' }} onClick={() => { setEditingId(t._id); setEditForm({name:t.name,category:t.category,price:t.price,description:t.description||'',isActive:t.isActive!==false}); }}>Edit</button>
                      <button className="btn btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0', background:'var(--color-danger-soft)', color:'var(--color-danger)', border:'none' }} onClick={() => setDeleteTarget(t)}>Del</button>
                    </div>
                  </>
                )}
              </div>
              </div>
            ))}
          </div>
      )}
      {!loading && treatments.length > 0 && groupBy === 'none' && listMode === 'compact' && (
        <div className="card" style={{ overflow:'hidden' }}>
          {treatments.map((t,i) => {
            const catStyle = CAT_COLORS[t.category] || CAT_COLORS.other;
            return (
              <div key={t._id} style={{ padding: editingId===t._id ? '10px 14px' : '9px 14px', borderTop:i===0?'none':'1px solid var(--color-border)', background:i%2===0?'transparent':'var(--color-surface)', opacity:t.isActive?1:0.6 }}>
                {editingId === t._id ? (
                  <GridEditForm t={t} editForm={editForm} setEditForm={setEditForm} handleUpdate={handleUpdate} setEditingId={setEditingId} saving={saving} CATEGORIES={CATEGORIES} />
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:6, height:28, borderRadius:3, background:catStyle.color, flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
                        {t.name}
                        {!t.isActive && <span style={{ fontSize:10, color:'var(--color-ink-faint)', fontWeight:400 }}>(inactive)</span>}
                      </div>
                      {t.description && <div style={{ fontSize:11, color:'var(--color-ink-faint)', marginTop:1 }}>{t.description}</div>}
                    </div>
                    <span style={{ fontSize:11, color:catStyle.color, fontWeight:600, background:catStyle.bg, padding:'2px 8px', borderRadius:10, flexShrink:0 }}>{t.category}</span>
                    <span style={{ fontWeight:800, fontSize:14, color:'var(--color-primary)', minWidth:64, textAlign:'right', flexShrink:0 }}>₹{(t.price||0).toLocaleString('en-IN')}</span>
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <button className="btn btn-ghost btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => { setEditingId(t._id); setEditForm({name:t.name,category:t.category,price:t.price,description:t.description||'',isActive:t.isActive!==false}); }}>
                        Edit
                      </button>
                      <button className="btn btn-sm" style={{ padding:'3px 8px', fontSize:11, background:'var(--color-danger-soft)', color:'var(--color-danger)', border:'none' }} onClick={() => setDeleteTarget(t)}>
                        Del
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {!loading && treatments.length > 0 && groupBy === 'none' && (!listMode || listMode === 'list') && (
        <div style={{ display: 'grid', gap: 8 }}>
          {treatments.map(t => {
            const catStyle = CAT_COLORS[t.category] || CAT_COLORS.other;
            return (
              <TreatmentCard key={t._id} t={t} catStyle={catStyle} editingId={editingId} setEditingId={setEditingId} editForm={editForm} setEditForm={setEditForm} handleUpdate={handleUpdate} setDeleteTarget={setDeleteTarget} saving={saving} CATEGORIES={CATEGORIES} />
            );
          })}
        </div>
      )}

      {/* Grouped view (by category) */}
      {!loading && treatments.length > 0 && groupBy !== 'none' && (
        (() => {
          const groups = {};
          treatments.forEach(t => {
            const k = t.category || 'other';
            const label = CATEGORIES.find(c => c.value === k)?.label || k;
            if (!groups[label]) groups[label] = [];
            groups[label].push(t);
          });
          return (
            <div style={{ display:'grid', gap:16 }}>
              {Object.entries(groups).map(([label, items]) => {
                const catStyle = CAT_COLORS[items[0]?.category] || CAT_COLORS.other;
                return (
                  <div key={label} className="card" style={{ overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:catStyle.bg, borderBottom:`1px solid var(--color-border)` }}>
                      <Layers size={13} color={catStyle.color}/>
                      <span style={{ fontWeight:700, fontSize:13, color:catStyle.color }}>{label}</span>
                      <span style={{ fontSize:12, color:'var(--color-ink-faint)' }}>· {items.length} treatment{items.length !== 1 ? 's' : ''}</span>
                    </div>
                    {listMode === 'grid' ? (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:8, padding:12 }}>
                        {items.map((t, i) => {
                          return (
                            <div key={t._id} className="tilt3d-scene stagger-in" style={{ '--stagger-i': i }}>
                            <div className="tilt3d-card" style={{ padding:12, border:'1px solid var(--color-border)', borderRadius:8, display:'flex', flexDirection:'column', gap:6, opacity:t.isActive?1:0.6 }}>
                              {editingId === t._id ? (
                                <GridEditForm t={t} editForm={editForm} setEditForm={setEditForm} handleUpdate={handleUpdate} setEditingId={setEditingId} saving={saving} CATEGORIES={CATEGORIES} />
                              ) : (
                                <>
                                  <div style={{ fontWeight:700, fontSize:13 }}>{t.name}</div>
                                  {t.description && <div style={{ fontSize:11, color:'var(--color-ink-faint)' }}>{t.description}</div>}
                                  <div style={{ fontWeight:800, fontSize:18, color:'var(--color-primary)', marginTop:'auto' }}>₹{(t.price||0).toLocaleString('en-IN')}</div>
                                  <div style={{ display:'flex', gap:4 }}>
                                    <button className="btn btn-outline btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0' }} onClick={() => { setEditingId(t._id); setEditForm({name:t.name,category:t.category,price:t.price,description:t.description||'',isActive:t.isActive!==false}); }}>Edit</button>
                                    <button className="btn btn-sm" style={{ flex:1, fontSize:11, padding:'3px 0', background:'var(--color-danger-soft)', color:'var(--color-danger)', border:'none' }} onClick={() => setDeleteTarget(t)}>Del</button>
                                  </div>
                                </>
                              )}
                            </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : listMode === 'compact' ? (
                      <div>
                        {items.map((t,i) => {
                          const cs = CAT_COLORS[t.category] || CAT_COLORS.other;
                          return (
                            <div key={t._id} style={{ padding: editingId===t._id ? '10px 14px' : '9px 14px', borderTop:i===0?'none':'1px solid var(--color-border)', background:i%2===0?'transparent':'var(--color-surface)', opacity:t.isActive?1:0.6 }}>
                              {editingId === t._id ? (
                                <GridEditForm t={t} editForm={editForm} setEditForm={setEditForm} handleUpdate={handleUpdate} setEditingId={setEditingId} saving={saving} CATEGORIES={CATEGORIES} />
                              ) : (
                                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                  <div style={{ width:5, height:24, borderRadius:3, background:cs.color, flexShrink:0 }}/>
                                  <div style={{ flex:1, minWidth:0 }}>
                                    <span style={{ fontWeight:700, fontSize:13 }}>{t.name}</span>
                                    {!t.isActive && <span style={{ marginLeft:6, fontSize:10, color:'var(--color-ink-faint)' }}>(inactive)</span>}
                                    {t.description && <span style={{ marginLeft:8, fontSize:11, color:'var(--color-ink-faint)' }}>{t.description}</span>}
                                  </div>
                                  <span style={{ fontWeight:800, fontSize:14, color:'var(--color-primary)', minWidth:60, textAlign:'right' }}>₹{(t.price||0).toLocaleString('en-IN')}</span>
                                  <button className="btn btn-ghost btn-sm" style={{ padding:'3px 8px', fontSize:11 }} onClick={() => { setEditingId(t._id); setEditForm({name:t.name,category:t.category,price:t.price,description:t.description||'',isActive:t.isActive!==false}); }}>Edit</button>
                                  <button className="btn btn-sm" style={{ padding:'3px 8px', fontSize:11, background:'var(--color-danger-soft)', color:'var(--color-danger)', border:'none' }} onClick={() => setDeleteTarget(t)}>Del</button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                    <div>
                      {items.map((t,i) => {
                        const cs = CAT_COLORS[t.category] || CAT_COLORS.other;
                        return (
                          <div key={t._id} style={{ borderTop: i===0?'none':'1px solid var(--color-border)' }}>
                            <TreatmentCard t={t} catStyle={cs} editingId={editingId} setEditingId={setEditingId} editForm={editForm} setEditForm={setEditForm} handleUpdate={handleUpdate} setDeleteTarget={setDeleteTarget} saving={saving} CATEGORIES={CATEGORIES} plain />
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()
      )}

            <ConfirmDialog
        open={!!deleteTarget}
        title={`Remove "${deleteTarget?.name}"?`}
        message="This removes it from the catalog. Patient records that used this treatment are not affected."
        confirmLabel={deleting ? 'Removing…' : 'Remove'}
        danger onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)}
      />
    </AppLayout>
  );
}
